import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

const CaseStudiesContainer = styled.section`
  min-height: 100vh;
  padding: 5rem 2rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
`;

const Content = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;
`;

const CasesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
`;

const CaseCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
`;

const CaseTitle = styled.h3`
  font-size: 1.3rem;
  color: #3b82f6;
  margin-bottom: 1rem;
  font-weight: 700;
`;

const CaseLocation = styled.div`
  color: #94a3b8;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const CaseMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Metric = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 1rem;
  border-radius: 10px;
  text-align: center;
`;

const MetricValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #10b981;
  margin-bottom: 0.5rem;
`;

const MetricLabel = styled.div`
  color: #64748b;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CaseDescription = styled.p`
  color: #94a3b8;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const Testimonial = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-left: 3px solid #3b82f6;
  padding: 1rem;
  border-radius: 8px;
  font-style: italic;
  color: #e2e8f0;
`;

function CaseStudies() {
  const caseStudies = [
    {
      id: 1,
      title: 'Mumbai High-Rise Residential Complex',
      location: 'Mumbai, Maharashtra, India',
      type: '42-floor residential building',
      metrics: {
        savings: '₹2.8 Crores',
        reduction: '18.2%',
        connections: '12,500',
        timeline: '35 days faster'
      },
      description: 'L&T Construction utilized threaded and swaged couplers for column-beam connections and shear wall vertical connections, achieving unprecedented material savings and construction speed.',
      testimonial: '"The use of rebar couplers transformed our construction approach, delivering unprecedented speed and quality." - Rajesh Kumar, Project Manager'
    },
    {
      id: 2,
      title: 'Delhi Metro Line Extension',
      location: 'New Delhi, India',
      type: '12.5km metro rail infrastructure',
      metrics: {
        savings: '40% Faster',
        reduction: '±2%',
        connections: '25,000',
        timeline: '100-year life'
      },
      description: 'AFCONS Infrastructure used grout-filled and threaded couplers for tunnel lining and station structural connections with enhanced seismic resistance and waterproofing.',
      testimonial: '"Critical infrastructure demands the highest reliability - rebar couplers delivered beyond expectations." - Metro Project Director'
    },
    {
      id: 3,
      title: 'Bangalore IT Park Complex',
      location: 'Bangalore, Karnataka, India',
      type: '6-building IT campus (LEED Platinum)',
      metrics: {
        savings: '₹1.2 Crores',
        reduction: '16.8%',
        connections: '8,500',
        timeline: '3 weeks early'
      },
      description: 'Sobha Developers achieved LEED Platinum certification with sustainable construction practices, using threaded couplers for transfer beams and long-span floor systems.',
      testimonial: '"Sustainability and efficiency combined - rebar couplers were essential for our green building goals." - Sustainability Manager'
    },
    {
      id: 4,
      title: 'Ahmedabad Bridge Rehabilitation',
      location: 'Ahmedabad, Gujarat, India',
      type: '850m bridge strengthening project',
      metrics: {
        savings: '75% Load Increase',
        reduction: '60% vs replacement',
        connections: '3,200',
        timeline: '50 years extended'
      },
      description: 'Gammon India used adhesive and post-installed threaded couplers to increase load rating from 40T to 70T with minimal traffic disruption.',
      testimonial: '"Rehabilitation over replacement - couplers made the impossible possible." - Bridge Engineer'
    },
    {
      id: 5,
      title: 'Chennai Port Container Terminal',
      location: 'Chennai, Tamil Nadu, India',
      type: 'Marine infrastructure (1200m wharf)',
      metrics: {
        savings: '₹15 Crores lifecycle',
        reduction: '90% maintenance',
        connections: '15,000',
        timeline: '100-year design'
      },
      description: 'Larsen & Toubro used marine-grade stainless steel couplers with cathodic protection for aggressive marine environment with 2.4 million TEU/year capacity.',
      testimonial: '"Marine environments demand the ultimate in corrosion resistance - stainless steel couplers delivered." - Marine Engineer'
    }
  ];

  return (
    <CaseStudiesContainer id="case-studies" data-section="case-studies">
      <Content>
        <SectionTitle
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Success Stories
        </SectionTitle>

        <CasesGrid>
          {caseStudies.map((caseStudy, index) => (
            <CaseCard
              key={caseStudy.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2 }}
              whileHover={{ scale: 1.02 }}
            >
              <CaseTitle>{caseStudy.title}</CaseTitle>
              <CaseLocation>📍 {caseStudy.location}</CaseLocation>
              <CaseDescription>{caseStudy.description}</CaseDescription>
              
              <CaseMetrics>
                <Metric>
                  <MetricValue>{caseStudy.metrics.savings}</MetricValue>
                  <MetricLabel>Total Savings</MetricLabel>
                </Metric>
                <Metric>
                  <MetricValue>{caseStudy.metrics.reduction}</MetricValue>
                  <MetricLabel>Material Reduction</MetricLabel>
                </Metric>
                <Metric>
                  <MetricValue>{caseStudy.metrics.connections}</MetricValue>
                  <MetricLabel>Connections</MetricLabel>
                </Metric>
                <Metric>
                  <MetricValue>{caseStudy.metrics.timeline}</MetricValue>
                  <MetricLabel>Timeline Impact</MetricLabel>
                </Metric>
              </CaseMetrics>

              <Testimonial>
                {caseStudy.testimonial}
              </Testimonial>
            </CaseCard>
          ))}
        </CasesGrid>
      </Content>
    </CaseStudiesContainer>
  );
}

export default CaseStudies;